import { useState } from "react"
import { ImageLightboxDialog } from "@/components/image-lightbox-dialog"
import {
  detectMediaPreviewUrl,
  mediaPreviewHintForKey,
  redactGenerationLogPayload,
  type MediaPreviewKind,
  type ResponsePayload,
  type WireExchange,
} from "@/lib/format-generation-log-payload"
import { cn } from "@/lib/utils"

type LightboxState = { url: string; kind: MediaPreviewKind }

type GenerationLogJsonViewProps = {
  value: unknown
  className?: string
}

function JsonString({
  value,
  mediaHint,
  onOpenMedia,
}: {
  value: string
  mediaHint?: MediaPreviewKind
  onOpenMedia: (state: LightboxState) => void
}) {
  const media = detectMediaPreviewUrl(value, mediaHint)
  if (media) {
    return (
      <button
        type="button"
        className="text-left break-all text-ui-link underline decoration-ui-link/40 underline-offset-2 hover:decoration-ui-link"
        title="Открыть превью"
        onClick={() => onOpenMedia(media)}
      >
        {JSON.stringify(value)}
      </button>
    )
  }
  return <span className="break-all text-ui-text-secondary">{JSON.stringify(value)}</span>
}

function JsonNode({
  value,
  depth,
  mediaHint,
  onOpenMedia,
}: {
  value: unknown
  depth: number
  mediaHint?: MediaPreviewKind
  onOpenMedia: (state: LightboxState) => void
}) {
  if (value === null) return <span className="text-ui-text-muted">null</span>
  if (typeof value === "boolean" || typeof value === "number") {
    return <span className="text-ui-accent-text">{String(value)}</span>
  }
  if (typeof value === "string") {
    return <JsonString value={value} mediaHint={mediaHint} onOpenMedia={onOpenMedia} />
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return <span>[]</span>
    return (
      <span>
        {"[\n"}
        {value.map((item, i) => (
          <span key={i}>
            {"  ".repeat(depth + 1)}
            <JsonNode value={item} depth={depth + 1} mediaHint={mediaHint} onOpenMedia={onOpenMedia} />
            {i < value.length - 1 ? "," : ""}
            {"\n"}
          </span>
        ))}
        {"  ".repeat(depth)}
        {"]"}
      </span>
    )
  }
  if (typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>)
    if (entries.length === 0) return <span>{"{}"}</span>
    return (
      <span>
        {"{\n"}
        {entries.map(([k, v], i) => (
          <span key={k}>
            {"  ".repeat(depth + 1)}
            <span className="text-ui-text-primary">{JSON.stringify(k)}</span>
            {": "}
            <JsonNode
              value={v}
              depth={depth + 1}
              mediaHint={mediaPreviewHintForKey(k, mediaHint)}
              onOpenMedia={onOpenMedia}
            />
            {i < entries.length - 1 ? "," : ""}
            {"\n"}
          </span>
        ))}
        {"  ".repeat(depth)}
        {"}"}
      </span>
    )
  }
  return <span>{String(value)}</span>
}

function JsonTree({ value, onOpenMedia }: { value: unknown; onOpenMedia: (state: LightboxState) => void }) {
  return <JsonNode value={redactGenerationLogPayload(value)} depth={0} onOpenMedia={onOpenMedia} />
}

function LogLightbox({
  lightbox,
  setLightbox,
}: {
  lightbox: LightboxState | null
  setLightbox: (state: LightboxState | null) => void
}) {
  return (
    <ImageLightboxDialog
      open={lightbox != null}
      onOpenChange={(open) => {
        if (!open) setLightbox(null)
      }}
      url={lightbox?.kind === "image" ? lightbox.url : null}
      videoUrl={lightbox?.kind === "video" ? lightbox.url : null}
      audioUrl={lightbox?.kind === "audio" ? lightbox.url : null}
      title={
        lightbox?.kind === "video" ? "Превью видео" : lightbox?.kind === "audio" ? "Превью аудио" : "Превью изображения"
      }
      nested={2}
    />
  )
}

/** Pretty JSON with clickable http(s) media URLs → ImageLightboxDialog. */
export function GenerationLogJsonView({ value, className }: GenerationLogJsonViewProps) {
  const [lightbox, setLightbox] = useState<LightboxState | null>(null)

  return (
    <>
      <pre
        className={cn(
          "it2tv-scrollbar max-h-64 overflow-auto rounded-lg bg-ui-surface-sunken p-2 text-[10px] leading-relaxed text-ui-text-secondary",
          className,
        )}
      >
        <JsonTree value={value} onOpenMedia={setLightbox} />
      </pre>
      <LogLightbox lightbox={lightbox} setLightbox={setLightbox} />
    </>
  )
}

function ExchangeBlock({
  ex,
  index,
  onOpenMedia,
}: {
  ex: WireExchange
  index: number
  onOpenMedia: (state: LightboxState) => void
}) {
  if (ex.kind === "poll") {
    const polls = 1 + (ex.repeat_count ?? 0)
    return (
      <div className="mb-3 last:mb-0">
        <p className="font-medium text-ui-text-primary">
          [{index + 1}] POLL {ex.verb || "GET"} {ex.url || ""}
        </p>
        <p className="text-ui-text-muted">status: {ex.task_status || "—"}</p>
        <p className="text-ui-text-muted">
          polls: ×{polls} (first + {ex.repeat_count ?? 0} repeats)
        </p>
        <p className="text-ui-text-muted">last_polled_at: {ex.last_polled_at || "—"}</p>
        <p className="mt-1 text-ui-text-muted">first_response:</p>
        <div className="pl-2">
          <JsonTree value={ex.first_response ?? null} onOpenMedia={onOpenMedia} />
        </div>
      </div>
    )
  }

  return (
    <div className="mb-3 last:mb-0">
      <p className="font-medium text-ui-text-primary">
        [{index + 1}] {ex.verb || "?"} {ex.url || ""}
      </p>
      {ex.request != null ? (
        <>
          <p className="mt-1 text-ui-text-muted">request:</p>
          <div className="pl-2">
            <JsonTree value={ex.request} onOpenMedia={onOpenMedia} />
          </div>
        </>
      ) : null}
      {ex.response != null ? (
        <>
          <p className="mt-1 text-ui-text-muted">response:</p>
          <div className="pl-2">
            <JsonTree value={ex.response} onOpenMedia={onOpenMedia} />
          </div>
        </>
      ) : null}
    </div>
  )
}

/** Response payload: human-readable poll groups + clickable media URLs. */
export function GenerationLogResponseView({ value, className }: GenerationLogJsonViewProps) {
  const [lightbox, setLightbox] = useState<LightboxState | null>(null)
  const redacted = redactGenerationLogPayload(value)

  const exchanges =
    redacted && typeof redacted === "object" && !Array.isArray(redacted)
      ? (redacted as ResponsePayload).exchanges
      : undefined

  return (
    <>
      <pre
        className={cn(
          "it2tv-scrollbar max-h-64 overflow-auto rounded-lg bg-ui-surface-sunken p-2 text-[10px] leading-relaxed text-ui-text-secondary",
          className,
        )}
      >
        {Array.isArray(exchanges) && exchanges.length > 0 ? (
          exchanges.map((ex, i) => (
            <ExchangeBlock key={i} ex={ex as WireExchange} index={i} onOpenMedia={setLightbox} />
          ))
        ) : (
          <JsonTree value={value} onOpenMedia={setLightbox} />
        )}
      </pre>
      <LogLightbox lightbox={lightbox} setLightbox={setLightbox} />
    </>
  )
}
